import { Paper, Stack } from '@mantine/core';
import React from 'react';
import { useLocation } from 'react-router';
import { SidebarItem } from 'src/UI/page-nav/sidebar/types';
import { PageHeader } from './components/PageHeader';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import { FileDragDropProvider } from './context/FileDragDropContext';
import { useFileDragDrop } from './hooks/useFileDragDrop';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { UploadedFile } from '../hooks/file-uploads/types';
import { useFamilyFileActions } from './family/useFamilyFileActions';

type FileUploadLayoutProps = {
  children: React.ReactNode;
  title: string;
  showSearch?: boolean;
  collapsibleSearch?: boolean;
  searchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  onBack?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchResultCount?: number;
  searchPlaceholder?: string;
  actions?: React.ReactNode;
  hideHeader?: boolean;
  familyId?: string;
  familyName?: string;
  variant?: 'default' | 'compact';
  secondarySection?: React.ReactNode;
  secondaryTitle?: string;
};

export default function FileUploadLayout(props: FileUploadLayoutProps) {
  if (props.familyId) {
    return <FamilyFileUploadLayout {...props} familyId={props.familyId} />;
  }
  return <ClientFileUploadLayout {...props} />;
}

type LayoutShellProps = FileUploadLayoutProps & {
  base: string;
  files: UploadedFile[];
  onMoveFile: (file: UploadedFile, parentId: string | null | undefined) => Promise<void>;
};

function LayoutShell({
  children,
  title,
  showSearch,
  collapsibleSearch,
  searchOpen,
  onSearchOpenChange,
  onBack,
  searchValue,
  onSearchChange,
  searchResultCount,
  searchPlaceholder,
  actions,
  hideHeader = false,
  variant = 'default',
  secondarySection,
  secondaryTitle,
  base,
  files,
  onMoveFile,
}: LayoutShellProps) {
  const location = useLocation();
  const isCompact = variant === 'compact';

  const dragDropHook = useFileDragDrop({ allFiles: files, handleMoveFile: onMoveFile });

  const sidebarLinks: SidebarItem[] = [
    { label: 'Uploaded documents', href: `${base}/add` },
    {
      label: 'Generated documents',
      href: `${base}/generated`,
      defaultExpanded: true,
      children: [
        { label: 'Suitability reports', href: `${base}/suitability-reports` },
        { label: 'Meeting agendas', href: `${base}/meeting-agendas` },
        { label: 'Meeting notes', href: `${base}/meeting-notes` },
        { label: 'Data exports', href: `${base}/data-exports` },
      ],
    },
    { label: 'Filed documents', href: `${base}/files` },
  ];

  const mainContent = (
    <Stack gap={isCompact ? 'sm' : 'xxl'}>
      {!hideHeader && (
        <PageHeader
          title={title}
          showSearch={showSearch}
          collapsibleSearch={collapsibleSearch}
          searchOpen={searchOpen}
          onSearchOpenChange={onSearchOpenChange}
          onBack={onBack}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchResultCount={searchResultCount}
          searchPlaceholder={searchPlaceholder}
          actions={actions}
          variant={variant}
        />
      )}

      {children}
    </Stack>
  );

  if (isCompact) {
    const pageContent =
      secondarySection != null ? (
        <Stack gap="md">
          <Paper variant="content-section">{mainContent}</Paper>
          <Paper variant="content-section">
            <Stack gap="sm">
              {secondaryTitle != null && <PageHeader title={secondaryTitle} variant={variant} />}
              {secondarySection}
            </Stack>
          </Paper>
        </Stack>
      ) : (
        <Paper variant="content-section">{mainContent}</Paper>
      );

    return (
      <FileDragDropProvider contextValue={dragDropHook}>
        <PageWithSidebar links={sidebarLinks} activeUrl={location.pathname} variant="documents">
          {pageContent}
        </PageWithSidebar>
      </FileDragDropProvider>
    );
  }

  const pageContent =
    secondarySection != null ? (
      <Stack gap="xxl">
        {mainContent}
        <Paper variant="content-section">
          <Stack gap="xxl">
            {secondaryTitle != null && <PageHeader title={secondaryTitle} variant={variant} />}
            {secondarySection}
          </Stack>
        </Paper>
      </Stack>
    ) : (
      mainContent
    );

  return (
    <FileDragDropProvider contextValue={dragDropHook}>
      <PageWithSidebar links={sidebarLinks} activeUrl={location.pathname} variant="documents">
        {pageContent}
      </PageWithSidebar>
    </FileDragDropProvider>
  );
}

function ClientFileUploadLayout(props: FileUploadLayoutProps) {
  const { clientId } = useCurrentClientIfAvailable();
  const { files, moveFile, refetchFiles } = useFileUploads();

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, targetId: parentId },
      );
    }
  };

  return (
    <LayoutShell
      {...props}
      base={`/client/${clientId}/document`}
      files={files}
      onMoveFile={handleMoveFile}
    />
  );
}

function FamilyFileUploadLayout(props: FileUploadLayoutProps & { familyId: string }) {
  const { familyId } = props;
  const { files, refetchFiles } = useFileUploads({ familyId });
  const { moveFile } = useFamilyFileActions({ familyId });

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { familyId, clientId: file.clientId, fileId: file.id, targetId: parentId },
      );
    }
  };

  return (
    <LayoutShell
      {...props}
      base={`/family/${familyId}/documents`}
      files={files}
      onMoveFile={handleMoveFile}
    />
  );
}
